import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useRef, RefObject, useCallback, useState, useEffect } from "react";
import { useReactToPrint } from "react-to-print";

const usePrint = (Name?: string) => {
  const componentRef: RefObject<HTMLDivElement> = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const printTimeoutRef = useRef<number | null>(null);

  const reactToPrintFn = useReactToPrint({
    contentRef: componentRef,
    documentTitle: Name ?? "",
    pageStyle: `
      @media print {
        @page {
          size: auto; /* auto is default, but ensures multipage layout */
          margin: 15mm;
        }

        html, body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
          background: white !important;
        }

        /* Hide everything except print section */
        body * {
          visibility: hidden !important;
        }
        #print-section, #print-section * {
          visibility: visible !important;
        }
        #print-section {
          position: relative !important;
          left: 0;
          top: 0;
          width: 100%;
        }

        /* Page break control */
        .page-break {
          page-break-before: always;
        }
        .avoid-break {
          page-break-inside: avoid;
        }
      }
    `,
  });

  useEffect(() => {
    return () => {
      if (printTimeoutRef.current !== null) {
        clearTimeout(printTimeoutRef.current);
      }
    };
  }, []);

  const handlePrint = useCallback(() => {
    const delayPrint = async () => {
      setIsOpen(true);
      setTimeout(() => {
        reactToPrintFn?.();
        printTimeoutRef.current = window.setTimeout(() => {
          setIsOpen(false);
          printTimeoutRef.current = null;
        }, 2000);
      }, 300);
    };

    delayPrint();
  }, [reactToPrintFn]);
  const handleDownloadPDF = useCallback(async () => {
    setIsOpen(true);
    const element = componentRef.current;
    if (!element) return;

    // store old styles
    const previousOverflow = element.style.overflow;
    const previousHeight = element.style.height;

    // expand element to full height before capture
    element.style.overflow = "visible";
    element.style.height = element.scrollHeight + "px";

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
    });

    // restore scroll container after capture
    element.style.overflow = previousOverflow;
    element.style.height = previousHeight;

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({
      orientation: "p",
      unit: "px",
      format: [canvas.width, canvas.height],
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = (canvas.height * pageWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);
    pdf.save(`${Name ?? "document"}.pdf`);
    // setIsOpen(false);
  }, [Name]);

  return { componentRef, handlePrint, handleDownloadPDF, isPrintMode: isOpen };
};
export default usePrint;